import { useEffect } from 'react';
import { create } from 'zustand';
import { useUserProfileStore } from 'stores/userProfileStore';
import {
  getActiveCompanyIdFromStorage,
  getCompanyIdFromLocationSearch,
  setActiveCompanyIdInStorage,
} from 'utils/activeCompanyStorageUtils';
import { CACHE_KEYS } from 'utils/constants';

export interface CompanySummary {
  id: string;
  name: string;
  owned: boolean;
  logoPath?: string;
}

/** Selector: returns the active company object from activeCompanyId + availableCompanies */
function selectActiveCompany(s: ActiveCompanyStore): CompanySummary | null {
  if (!s.activeCompanyId || !s.availableCompanies.length) return null;
  return s.availableCompanies.find((c) => c.id === s.activeCompanyId) ?? null;
}

/** Hook: returns the active company object (derived from activeCompanyId + availableCompanies) */
export function useActiveCompany(): CompanySummary | null {
  return useActiveCompanyStore(selectActiveCompany);
}

interface ActiveCompanyStore {
  activeCompanyId: string | null;
  isInitialized: boolean;
  availableCompanies: CompanySummary[];
  setActiveCompanyId: (companyId: string) => Promise<void>;
  reset: () => void;
  hydrate: (params: {
    activeCompanyId: string | null;
    availableCompanies: CompanySummary[];
  }) => void;
  setAvailableCompanies: (companies: CompanySummary[]) => void;
}

export const useActiveCompanyStore = create<ActiveCompanyStore>((set, get) => ({
  activeCompanyId: null,
  isInitialized: false,
  availableCompanies: [],

  setActiveCompanyId: async (companyId: string) => {
    const { availableCompanies } = get();
    const isValidCompany = availableCompanies.some((c) => c.id === companyId);
    if (!isValidCompany) {
      throw new Error('Company not found in user companies');
    }
    await setActiveCompanyIdInStorage(companyId);
    set({ activeCompanyId: companyId });
  },

  hydrate: ({ activeCompanyId, availableCompanies }) => {
    set({ activeCompanyId, availableCompanies, isInitialized: true });
  },

  reset: () => {
    set({
      activeCompanyId: null,
      availableCompanies: [],
      isInitialized: false,
    });
  },

  setAvailableCompanies: (availableCompanies) => {
    set({ availableCompanies });
  },
}));

export function useActiveCompanyInit(): void {
  const profileCompanies = useUserProfileStore((s) => s.profile?.companies);
  const hydrate = useActiveCompanyStore((s) => s.hydrate);
  const reset = useActiveCompanyStore((s) => s.reset);

  useEffect(() => {
    const availableCompanies = profileCompanies ?? [];
    if (availableCompanies.length === 0) {
      reset();
      return;
    }

    const init = async () => {
      const orderedCompanyIds = availableCompanies.map((company) => company.id);
      const companyIdFromUrl =
        typeof window !== 'undefined'
          ? getCompanyIdFromLocationSearch(
              window.location.search,
              orderedCompanyIds,
            )
          : null;

      if (companyIdFromUrl) {
        const isUrlCompanyAllowed = availableCompanies.some(
          (c) => c.id === companyIdFromUrl,
        );
        if (isUrlCompanyAllowed) {
          await setActiveCompanyIdInStorage(companyIdFromUrl);
          hydrate({ activeCompanyId: companyIdFromUrl, availableCompanies });
          return;
        }
      }

      let storedCompanyId: string | null = null;
      try {
        storedCompanyId = await getActiveCompanyIdFromStorage();
      } catch (error) {
        console.warn('Failed to decrypt stored company ID, clearing:', error);
        localStorage.removeItem(CACHE_KEYS.ACTIVE_COMPANY_ID);
      }

      if (storedCompanyId) {
        const isValidCompany = availableCompanies.some(
          (c) => c.id === storedCompanyId,
        );
        if (isValidCompany) {
          hydrate({ activeCompanyId: storedCompanyId, availableCompanies });
          return;
        }
      }

      const ownedCompany = availableCompanies.find((c) => c.owned);
      const defaultCompanyId = ownedCompany
        ? ownedCompany.id
        : availableCompanies[0].id;
      await setActiveCompanyIdInStorage(defaultCompanyId);
      hydrate({ activeCompanyId: defaultCompanyId, availableCompanies });
    };

    void init();
  }, [profileCompanies, hydrate, reset]);
}
